import React, { useEffect, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { NavLink, useLocation } from "react-router-dom";
import { HiChevronDoubleLeft, HiChevronDoubleRight } from "react-icons/hi2";
import BrandLogo from "../BrandLogo/BrandLogo";
import NavGroup from "./NavGroup";
import useNavContext from "../../hooks/useNavContext";

const COLLAPSED_W = 72;
const EXPANDED_W  = 160;

// `overlay` is true on tablets: the sidebar stays a 72px rail and expanding it
// floats over the content (with a scrim) rather than pushing it aside.
const Sidebar = ({ isOpen, vendorData, onCollapseChange, overlay = false }) => {
  // What this person may see is resolved once in useNavContext (shared with the
  // phone bottom bar + menu sheet) from the tested getNavGroups() config.
  const { navGroups } = useNavContext(vendorData);

  // Start from the person's saved choice (it was written on every toggle but never
  // read back, so every reload re-collapsed it). Tablets always start as the rail.
  const [collapsed, setCollapsed] = useState(() => {
    if (overlay) return true;
    try {
      const saved = localStorage.getItem("sidebarCollapsed");
      if (saved !== null) return saved === "true";
    } catch {
      /* storage blocked - fall through to the default */
    }
    return true;
  });
  const location = useLocation();
  // Auto-collapse/expand when window resizes across the 1024px boundary
  // only if user hasn't explicitly toggled (no localStorage entry)
  useEffect(() => {
    const onResize = () => {
      if (localStorage.getItem("sidebarCollapsed") === null) {
        setCollapsed(window.innerWidth < 1024);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    onCollapseChange?.(collapsed);
  }, [collapsed, onCollapseChange]);

  // Overlay mode: rotating onto a tablet width, or picking a destination, folds
  // the sidebar back to the rail so it never sits over the page you just opened.
  useEffect(() => {
    if (overlay) setCollapsed(true);
  }, [overlay, location.pathname]);

const toggle = useCallback(() => {
  setCollapsed((prev) => {
    const next = !prev;
    // An overlay toggle is momentary - it must not overwrite the desktop preference.
    if (!overlay) localStorage.setItem("sidebarCollapsed", String(next));
    return next;
  });
}, [overlay]);

  // ── styles ──────────────────────────────────────────────────────────────
  const navItemStyle = (isActive) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: collapsed ? "0px" : "5px",
    padding: collapsed ? "13px 4px" : "10px 6px",
    borderRadius: "10px",
    margin: "2px 6px",
    textDecoration: "none",
    cursor: "pointer",
    transition: "background 0.2s, box-shadow 0.2s, color 0.2s",
    background: isActive
      ? "linear-gradient(135deg, #C2185B 0%, #E91E63 100%)"
      : "transparent",
    color: isActive ? "#ffffff" : "#6c757d",
    boxShadow: isActive ? "0 4px 12px rgba(194,24,91,0.28)" : "none",
    minHeight: "54px",
    overflow: "hidden",
  });

  const labelStyle = {
    fontSize: "10px",
    fontWeight: 600,
    letterSpacing: "0.03em",
    lineHeight: 1.2,
    textAlign: "center",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "130px",
    // animate out/in
    opacity: collapsed ? 0 : 1,
    maxHeight: collapsed ? "0px" : "16px",
    transition: "opacity 0.2s ease, max-height 0.22s ease",
    pointerEvents: "none",
  };

  const iconSize = 22;


  return (
    <>
    {overlay &&
      !collapsed &&
      ReactDOM.createPortal(
        <div className="sidebar-scrim" onClick={() => setCollapsed(true)} />,
        document.body
      )}
    <style>{`
      .sidebar-nav-scroll::-webkit-scrollbar { display: none; }
      .sidebar-nav-scroll { -ms-overflow-style: none; scrollbar-width: none; }
    `}</style>
    <div
      className={`sidebar bg-white border-end ${isOpen ? "open" : ""}`}
      style={{
        width: collapsed ? `${COLLAPSED_W}px` : `${EXPANDED_W}px`,
        transition: "width 0.25s ease",
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
          paddingTop: "8px",
          paddingBottom: "12px",
        }}
      >
        {/* ── top: toggle + nav ── */}
        <div className="sidebar-nav-scroll" style={{ flex: "1 1 auto", overflowY: "auto", overflowX: "hidden" }}>
          {/* Toggle button */}
          <div
            style={{
              display: "flex",
              justifyContent: collapsed ? "center" : "flex-end",
              padding: "4px 8px 8px",
              borderBottom: "1px solid #f3f4f6",
              marginBottom: "6px",
            }}
          >
            <button
              onClick={toggle}
              className="sidebar-toggle"
              aria-label={collapsed ? "Expand menu" : "Collapse menu"}
              aria-expanded={!collapsed}
              title={collapsed ? "Expand" : "Collapse"}
              style={{
                background: "none",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                padding: "5px 7px",
                cursor: "pointer",
                color: "#adb5bd",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s",
                lineHeight: 1,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#fdf2f5";
                e.currentTarget.style.borderColor = "#C2185B";
                e.currentTarget.style.color = "#C2185B";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "none";
                e.currentTarget.style.borderColor = "#e5e7eb";
                e.currentTarget.style.color = "#adb5bd";
              }}
            >
              {collapsed ? (
                <HiChevronDoubleRight size={13} />
              ) : (
                <HiChevronDoubleLeft size={13} />
              )}
            </button>
          </div>

          {/* Nav items - structure/gating rules live in sidebarNavConfig.js,
              the single source of truth that replaced the three
              hand-duplicated branches (isTeamUser / isSportsService /
              default) that used to be inline here. */}
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {navGroups.map((entry) =>
              entry.type === "link" ? (
                <li key={entry.key}>
                  <NavLink to={entry.to} style={({ isActive }) => navItemStyle(isActive)}>
                    <entry.icon size={iconSize} />
                    <span style={labelStyle}>{entry.label}</span>
                  </NavLink>
                </li>
              ) : (
                <NavGroup
                  key={entry.key}
                  group={entry}
                  collapsed={collapsed}
                  navItemStyle={navItemStyle}
                  labelStyle={labelStyle}
                  iconSize={iconSize}
                />
              )
            )}
          </ul>
        </div>

        {/* ── bottom: logo ── */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "8px",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          <BrandLogo variant={collapsed ? "icon" : "full"} height={collapsed ? 34 : 30} />
        </div>
      </div>
    </div>
    </>
  );
};

export default Sidebar;
